// src/utils/auth.ts
import { history } from '@umijs/max';
import { ensureMsalInitialized, msalInstance } from './msal'; // Use shared msalInstance
import { loginRequest } from '@/config/authConfig';
import { flushSync } from 'react-dom';
import { stringify } from 'querystring';
import { setToken, clearToken, getStoredToken } from './tokenManager';

// Re-export token management functions for backward compatibility
export { setToken, clearToken };
export const getToken = getStoredToken;

/**
 * Get fresh access token using MSAL.
 * @deprecated Use getValidToken from tokenManager instead
 */
export const getAccessToken = async (): Promise<string> => {
  const { getValidToken } = await import('./tokenManager');
  return getValidToken();
};

// clearToken is now imported from tokenManager

/**
 * Perform Azure login flow and save tokens.
 */
export const login = async (setInitialState: any) => {
  await ensureMsalInitialized();
  try {
    const response = await msalInstance.handleRedirectPromise();
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length > 0) {
      const account = accounts[0];
      const tokenResponse =
        response ??
        (await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account,
        }));

      const { accessToken, expiresOn } = tokenResponse;
      if (accessToken && expiresOn) {
        const expiresInSeconds = Math.floor((new Date(expiresOn).getTime() - Date.now()) / 1000);
        setToken(accessToken, expiresInSeconds);
      }

      const userInfo = {
        name: account.name || 'User',
        email: account.username,
        avatar: '/default-avatar.png',
        accessToken,
      };

      flushSync(() => {
        setInitialState((s: any) => ({
          ...s,
          currentUser: userInfo,
        }));
      });

      const urlParams = new URL(window.location.href).searchParams;
      const redirect = urlParams.get('redirect') || '/';
      history.push(redirect);
    } else {
      await msalInstance.loginRedirect(loginRequest);
    }
  } catch (err) {
    console.error('Azure login error:', err);
    await msalInstance.loginRedirect(loginRequest);
  }
};

/**
 * Perform logout: clear tokens and local state, but keep Azure AD session.
 * This provides better UX as users can silently login again if Azure AD session is still valid.
 */
export const logout = async (setInitialState: any) => {
  // Clear application state
  flushSync(() => {
    setInitialState((s: any) => ({ ...s, currentUser: undefined }));
  });

  // Clear local tokens
  clearToken();

  // Clear MSAL cache to ensure fresh token acquisition on next login
  try {
    await ensureMsalInitialized();
    msalInstance.clearCache();
  } catch (err) {
    console.warn('Failed to clear MSAL cache:', err);
  }

  // Redirect to login page
  history.replace({
    pathname: '/user/login',
    search: stringify({
      redirect: window.location.pathname + window.location.search,
    }),
  });
};

/**
 * Perform full logout: clear tokens, logout from Azure AD completely.
 * Use this when you need to completely sign out from Azure AD (e.g., on shared devices).
 */
export const fullLogout = async (setInitialState: any) => {
  await ensureMsalInitialized();

  // Clear application state
  flushSync(() => {
    setInitialState((s: any) => ({ ...s, currentUser: undefined }));
  });

  // Clear local tokens
  clearToken();

  // Logout from Azure AD completely
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    try {
      await msalInstance.logoutRedirect({
        account: accounts[0],
        postLogoutRedirectUri: window.location.origin + '/user/login',
      });
    } catch (err) {
      console.error('Azure logout error:', err);
      history.replace({
        pathname: '/user/login',
        search: stringify({
          redirect: window.location.pathname + window.location.search,
        }),
      });
    }
  } else {
    history.replace({
      pathname: '/user/login',
      search: stringify({
        redirect: window.location.pathname + window.location.search,
      }),
    });
  }
};
